import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { PageShell } from '@/components/portal/page-header'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useTabQueryParam } from '@/hooks/use-tab-query-param'
import { ProfileIdentityCard } from './profile-identity-card'
import { ProfileStatCards, buildProfileStats } from './profile-stat-cards'
import { ProfileTabNav } from './profile-tab-nav'
import { MemberBillingTab } from './member-billing-tab'
import { ProfileOverviewPanel } from './profile-overview-panel'
import { ProfilePlotsPanel } from './profile-plots-panel'
import { ProfileActivityPanel } from './profile-activity-panel'
import { ProfileSettingsPanel } from './profile-settings-panel'
import { ProfileAppointmentPanel } from './profile-appointment-panel'
import { ProfileSkeleton } from './profile-skeleton'
import { getAvailableTabs, type ProfileTabId } from './profile-utils'

const ALL_PROFILE_TABS: ProfileTabId[] = ['overview', 'billing', 'plots', 'settings', 'activity']
import { normalizeKenyaPhone } from '@riverpark/utils/member-import'
import { isStaffRole } from '@riverpark/types'
import type { ManageableMemberStatus, MemberDetail } from '@riverpark/types'

export interface MemberProfileViewProps {
  profile: MemberDetail | undefined
  isLoading: boolean
  error: Error | null
  isSelf: boolean
  /** Breadcrumb parent link e.g. Members list */
  backLink?: { to: string; label: string }
  pageTitle?: string
  canManageRole: boolean
  canManagePlots: boolean
  /** Base path for plot detail links, e.g. /operations/plots */
  plotsBasePath?: string
  canDelete: boolean
  readOnly?: boolean
  onSave: (data: {
    name: string
    email: string
    phone: string
    role: MemberDetail['role']
    /** Omitted when invited member has no admin status override */
    status?: ManageableMemberStatus
  }) => Promise<void>
  onDeactivate?: () => Promise<void>
  onResendInvite?: () => Promise<void>
  isSavePending: boolean
  isDeactivatePending: boolean
  isResendPending: boolean
}

export function MemberProfileView({
  profile,
  isLoading,
  error,
  isSelf,
  backLink,
  pageTitle,
  canManageRole,
  canManagePlots,
  plotsBasePath,
  canDelete,
  readOnly = false,
  onSave,
  onDeactivate,
  onResendInvite,
  isSavePending,
  isDeactivatePending,
  isResendPending,
}: MemberProfileViewProps) {
  // Staff roles (committee/estate_manager/admin) have no dedicated self-service billing page of
  // their own — unlike plain members, who already have /bills — so their own profile is the only
  // place they can see their own invoices/credit notes/payments if they also own a plot.
  const includeBilling = !isSelf || (profile !== undefined && isStaffRole(profile.role))
  const tabs = profile
    ? getAvailableTabs(profile, { includeSettings: isSelf || !readOnly, includeBilling })
    : (['overview', 'settings', 'activity'] as ProfileTabId[])
  const [activeTab, setActiveTab] = useTabQueryParam(ALL_PROFILE_TABS, 'overview')

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState<MemberDetail['role']>('member')
  const [status, setStatus] = useState<ManageableMemberStatus>('active')
  const [formError, setFormError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [phoneVerified, setPhoneVerified] = useState(true)
  const [emailVerified, setEmailVerified] = useState(true)

  useEffect(() => {
    if (!profile) return
    setName(profile.name)
    setEmail(profile.email ?? '')
    setPhone(profile.phone)
    setRole(profile.role)
    setStatus(profile.status === 'inactive' ? 'inactive' : 'active')
    setPhoneVerified(true)
    setEmailVerified(true)
  }, [profile])

  useEffect(() => {
    if (profile && !tabs.includes(activeTab)) {
      setActiveTab(tabs[0]!)
    }
  }, [profile, tabs, activeTab])

  async function handleResendInvite() {
    if (!onResendInvite) return
    setFormError(null)
    setResendSuccess(false)
    try {
      await onResendInvite()
      setResendSuccess(true)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to resend invitation')
    }
  }

  async function handleSave() {
    if (!profile) return

    const phoneChanging =
      isSelf && normalizeKenyaPhone(phone.trim()) !== normalizeKenyaPhone(profile.phone.trim())
    const emailChanging =
      isSelf && email.trim().toLowerCase() !== (profile.email ?? '').trim().toLowerCase()
    if (phoneChanging && !phoneVerified) {
      setFormError('Verify your new phone number before saving.')
      return
    }
    if (emailChanging && !emailVerified) {
      setFormError('Verify your new email address before saving.')
      return
    }

    setFormError(null)
    setSaved(false)
    try {
      const statusToSave = profile.status === 'invited' ? undefined : status
      await onSave({ name, email, phone, role, status: statusToSave })
      setSaved(true)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save')
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    void handleSave()
  }

  if (isLoading) {
    return (
      <PageShell>
        <ProfileSkeleton />
      </PageShell>
    )
  }

  if (error || !profile) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-destructive">
          {error?.message ?? 'Profile not found'}
        </p>
        {backLink && (
          <Link to={backLink.to} className="mt-4 inline-block font-sans text-sm text-primary">
            ← {backLink.label}
          </Link>
        )}
      </PageShell>
    )
  }

  const showPlotsTab = profile.role !== 'admin' && profile.role !== 'estate_manager'

  return (
    <PageShell>
      <nav className="flex flex-wrap items-center gap-1.5 font-sans text-sm text-muted-foreground">
        {backLink ? (
          <>
            <Link to={backLink.to} className="transition-colors hover:text-ink">
              {backLink.label}
            </Link>
            <ChevronRight size={14} className="shrink-0 opacity-50" aria-hidden />
            <span className="text-ink">{profile.name}</span>
          </>
        ) : (
          <>
            <span>Account</span>
            <ChevronRight size={14} className="shrink-0 opacity-50" aria-hidden />
            <span className="text-ink">{pageTitle ?? 'My profile'}</span>
          </>
        )}
      </nav>

      {backLink && (
        <Link
          to={backLink.to}
          className="mt-2 inline-flex items-center gap-1.5 font-sans text-sm text-muted-foreground transition-colors hover:text-ink"
        >
          <ArrowLeft size={16} />
          Back to {backLink.label.toLowerCase()}
        </Link>
      )}

      <header className="mt-4 space-y-1">
        <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
          {isSelf ? 'Account' : 'Member profile'}
        </p>
        <h1 className="font-serif text-title text-ink">{isSelf ? 'My profile' : profile.name}</h1>
      </header>

      <div className="grid gap-6 xl:grid-cols-[minmax(280px,320px)_1fr]">
        <div className="xl:sticky xl:top-8 xl:self-start">
          <ProfileIdentityCard profile={profile} />
        </div>

        <div className="min-w-0 space-y-6">
          <ProfileStatCards stats={buildProfileStats(profile)} />

          <Card className="overflow-hidden">
            <ProfileTabNav tabs={tabs} active={activeTab} onChange={setActiveTab} isSelf={isSelf} />

            <div className="p-6" role="tabpanel">
              {activeTab === 'overview' && (
                <ProfileOverviewPanel profile={profile} isSelf={isSelf} />
              )}

              {activeTab === 'billing' && includeBilling && (
                <MemberBillingTab memberId={profile.id} memberName={profile.name} />
              )}

              {activeTab === 'plots' && showPlotsTab && (
                <ProfilePlotsPanel
                  plots={profile.plots}
                  readOnly={!canManagePlots}
                  memberId={canManagePlots ? profile.id : undefined}
                  memberName={canManagePlots ? profile.name : undefined}
                  plotsBasePath={plotsBasePath}
                  plotHref={isSelf && !canManagePlots ? (plot) => `/plots/${plot.id}` : undefined}
                />
              )}

              {activeTab === 'settings' && (
                <ProfileSettingsPanel
                  name={name}
                  email={email}
                  phone={phone}
                  role={role}
                  status={status}
                  onNameChange={setName}
                  onEmailChange={setEmail}
                  onPhoneChange={setPhone}
                  onRoleChange={canManageRole ? setRole : undefined}
                  onStatusChange={canManageRole ? setStatus : undefined}
                  canManageRole={canManageRole}
                  canDelete={canDelete}
                  memberStatus={profile.status}
                  formError={formError}
                  saved={saved}
                  isPending={isSavePending}
                  isDeactivatePending={isDeactivatePending}
                  onSubmit={handleSubmit}
                  onDeactivate={onDeactivate}
                  onResendInvite={
                    canManageRole && profile.status === 'invited' && onResendInvite
                      ? () => void handleResendInvite()
                      : undefined
                  }
                  isResendPending={isResendPending}
                  resendSuccess={resendSuccess}
                  isSelf={isSelf}
                  savedPhone={profile.phone}
                  phoneVerified={phoneVerified}
                  onPhoneVerifiedChange={isSelf ? setPhoneVerified : undefined}
                  savedEmail={profile.email ?? ''}
                  emailVerified={emailVerified}
                  onEmailVerifiedChange={isSelf ? setEmailVerified : undefined}
                />
              )}

              {activeTab === 'activity' && <ProfileActivityPanel isSelf={isSelf} />}
            </div>
          </Card>

          {/* Committee position card — admin view of a committee member, settings tab only */}
          {activeTab === 'settings' && canManageRole && profile.role === 'committee' && (
            <Card>
              <CardHeader>
                <CardTitle>Committee position</CardTitle>
                <p className="font-sans text-sm text-muted-foreground">
                  Appoint this member to a committee position. Each position can only have one
                  active holder — appointing here automatically ends the previous holder's term.
                </p>
              </CardHeader>
              <CardContent>
                <ProfileAppointmentPanel
                  memberId={profile.id}
                  memberName={profile.name}
                  currentAppointment={profile.committeeAppointment}
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </PageShell>
  )
}
